import { Box, Stack, Button, ButtonProps, MenuItem } from "@mui/material";
import {
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
  GridToolbarProps,
  useGridApiContext,
} from "@mui/x-data-grid-pro";
import { useState, useRef } from "react";
import DownloadIcon from "@mui/icons-material/Download";
import Menu from "@mui/material/Menu";
import { testIds } from "./DataGridCustomToolbar.testIds";

type CustomToolbarProps = GridToolbarProps & {
  onExport?: () => void | Promise<void>;
};

const DataGridCustomToolbar = (props: CustomToolbarProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const open = Boolean(anchorEl);
  const apiRef = useGridApiContext();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleExportCSV = () => {
    handleClose();
    if (props.onExport) {
      // Use our custom export
      props.onExport();
    } else {
      // Fall back to DataGrid's export by triggering the default button
      // This won't happen with SmartLynksTable since it always provides onExport
    }
  };

  const handlePrint = () => {
    handleClose();
    // Use DataGrid's built-in print functionality
    apiRef.current.exportDataAsPrint();
  };

  return (
    <GridToolbarContainer>
      <Stack flex={1} direction="row" justifyContent="space-between">
        {props.showQuickFilter ? (
          <GridToolbarQuickFilter
            {...props.quickFilterProps}
            inputProps={{
              "data-testid": testIds.dataGridCustomToolbarQuickFilter,
            } as React.InputHTMLAttributes<HTMLInputElement>}
          />
        ) : (
          <Box />
        )}
        <Box>
          <GridToolbarColumnsButton
            slotProps={{
              button: {
                "data-testid": testIds.dataGridCustomToolbarColumns,
              } as ButtonProps & { "data-testid": string },
            }}
          />
          <GridToolbarFilterButton />
          {props.onExport ? (
            <>
              <Button data-testid={testIds.dataGridCustomToolbarExport}
                ref={buttonRef}
                size="small"
                startIcon={<DownloadIcon />}
                onClick={handleClick}
                aria-controls={open ? "export-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={open ? "true" : undefined}
              >
                Export
              </Button>
              <Menu
                id="export-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                  "aria-labelledby": "export-button",
                }}
              >
                <MenuItem data-testid={testIds.dataGridCustomToolbarDownloadAsCsv} onClick={handleExportCSV}>Download as CSV</MenuItem>
                <MenuItem data-testid={testIds.dataGridCustomToolbarPrint} onClick={handlePrint}>Print</MenuItem>
              </Menu>
            </>
          ) : (
            <GridToolbarExport />
          )}
        </Box>
      </Stack>
    </GridToolbarContainer>
  );
};

export default DataGridCustomToolbar;
